//src/web/pages/Gallery/GalleryEditPosting.tsx

import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import api from '../../../shared/api/axios';
import SidebarGalleryEditPosting from '../../sidebars/SidebarGalleryEditPosting';
import { useAuth } from '../../../shared/contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import HighLogo from '../../HighLogo';
import logo from "../../../assets/logo.png";
import Hls from "hls.js";
import Modal from "../../settings/Modal";


interface SidebarData {
  hashtags?: string[];
}

type ExtMediaItem =
  | {
      id: string;
      kind: 'image';
      url: string;
    }
  | {
      id: string;
      kind: 'video';
      videoId?: string | null;
      hlsUrl?: string | null;
      dashUrl?: string | null;
      posterUrl?: string | null;
      musicTitle?: string | null;
      musicArtist?: string | null;
      musicId?: string | null;
    };


const API_BASE = process.env.REACT_APP_API_BASE_URL!;
const API_ORIGIN = API_BASE.replace(/\/api\/?$/, '');


const resolveVideoSrc = (item: Extract<ExtMediaItem, { kind: 'video' }>) => {
  if (item.hlsUrl) return item.hlsUrl;
  if (item.videoId) return `${API_ORIGIN}/hls/playlist/${item.videoId}`;
  return '';
};

// Accepts string or object from server and preserves music meta
const fromServerMedia = (m: any, index: number): ExtMediaItem => {
  // HLS video (correct path)
if (m?.isHls === true || m?.videoId || m?.hlsUrl || m?.dashUrl) {
  return {
    id: String(m?._id ?? m?.videoId ?? m?.hlsUrl ?? `video-${index}`),
    kind: 'video',
    videoId: m.videoId ?? null,
    hlsUrl: m.hlsUrl ?? null,
    dashUrl: m.dashUrl ?? null,
    posterUrl: m.posterUrl ?? null,
    musicTitle: m.musicTitle ?? m.music?.title ?? null,
    musicArtist: m.musicArtist ?? m.music?.artist ?? null,
    musicId: m.musicId ?? m.music?._id ?? null,
  };
}

  // image fallback
  const url =
    m?.url ??
    m?.image?.url ??
    m?.src ??
    m?.path ??
    '';

  return {
    id: String(m?._id ?? url ?? `img-${index}`),
    kind: 'image',
    url: String(url),
  };
};


const MAX_TAGS = 8;

function normalizeTag(raw: string) {
  const cleaned = raw.trim();
  if (!cleaned) return '';
  const withHash = cleaned.startsWith('#') ? cleaned : `#${cleaned}`;
  return withHash.replace(/\s+/g, '').toLowerCase();
}

function normalizeTagList(tags: string[]) {
  return Array.from(
    new Set(tags.map(normalizeTag).filter(Boolean))
  ).slice(0, MAX_TAGS);
}


// HlsPlayer

export const HLSPlayer = ({
  src,
  poster,
  muted,
  active,
  style = {},
  onOrientation,
  isLandscape
}: {
  src: string;
  poster?: string;
  muted: boolean;
  active: boolean;
  style?: React.CSSProperties;
  onOrientation?: (isLandscape: boolean) => void;
  isLandscape?: boolean | null;
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);

  const [ready, setReady] = useState(false);
  const [showControls, setShowControls] = useState(false);
  const [progress, setProgress] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
const [isMuted, setIsMuted] = useState(muted);
const [userPaused, setUserPaused] = useState(false);

  const emitOrientation = (v: HTMLVideoElement) => {
    if (!v.videoWidth || !v.videoHeight) return;
    onOrientation?.(v.videoWidth > v.videoHeight);
  };

  //Play/pause
  const PlayIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="#bcb4b4"
  >
    <path d="M7 5v14l11-7z" />
  </svg>
);

const PauseIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="#bcb4b4"
  >
    <rect x="7.2" y="5" width="3.6" height="14" rx="1.2" />
    <rect x="13.2" y="5" width="3.6" height="14" rx="1.2" />
  </svg>
);

  //Loudspeakers
  const SpeakerOnIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#bbb"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
    <path d="M15 9a4 4 0 0 1 0 6" />
    <path d="M18 7a7 7 0 0 1 0 10" />
  </svg>
);


const SpeakerOffIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#bbb"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
    <line x1="23" y1="9" x2="17" y2="15" />
    <line x1="17" y1="9" x2="23" y2="15" />
  </svg>
);

  /* ---------------- HLS attach ---------------- */
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !src) return;

    let hls: Hls | null = null;
    setReady(false);

    if (video.canPlayType("application/vnd.apple.mpegurl")) {
      video.src = src;
    } else if (Hls.isSupported()) {
      hls = new Hls({ startLevel: 0, enableWorker: true });
      hls.loadSource(src);
      hls.attachMedia(video);
    }
  }, [src]);

  /* ---------------- autoplay logic ---------------- */
  useEffect(() => {
  const v = videoRef.current;
  if (!v) return;
  if (!active) return;

  if (!userPaused) {
    v.play().catch(() => {});
  }
}, [active, userPaused]);

  /* ---------------- progress tracking ---------------- */
  const onTimeUpdate = () => {
    const v = videoRef.current;
    if (!v || !v.duration) return;
    setProgress((v.currentTime / v.duration) * 100);
  };

  const handleScrub = (value: number) => {
    const v = videoRef.current;
    if (!v || !v.duration) return;
    v.currentTime = (v.duration * value) / 100;
  };

  /* ---------------- time ---------------- */
  const formatTime = (sec: number) => {
  if (!sec || isNaN(sec)) return "0:00";
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60).toString().padStart(2, "0");
  return `${m}:${s}`;
};


  /* ---------------- volume ---------------- */
  const handleVolume = (value: number) => {
    const v = videoRef.current;
    if (!v) return;
    v.volume = value;
    setVolume(value);
  };

  useEffect(() => {
  const v = videoRef.current;
  if (!v) return;
  v.muted = isMuted;
}, [isMuted]);

  /* ---------------- play/pause ---------------- */
  const togglePlay = () => {
  const v = videoRef.current;
  if (!v) return;

  if (!v.paused && !v.ended) {
    v.pause();
    setUserPaused(true);
  } else {
    v.play().catch(() => {});
    setUserPaused(false);
  }
};




  
  /* ---------------- render ---------------- */

 return (
  <div
    style={{
      position: "relative",
      width: "100%",
      height: "100%",
      overflow: "hidden",
    }}
    onMouseEnter={() => setShowControls(true)}
    onMouseLeave={() => setShowControls(false)}
  >
    {/* VIDEO */}
    <video
      ref={videoRef}
      poster={poster}
      muted={isMuted}
      playsInline
      preload="auto"
      controls={false}
      onPointerDown={togglePlay}
      onLoadedMetadata={(e) => {
        setReady(true);
        emitOrientation(e.currentTarget);
      }}
      onLoadedData={(e) => emitOrientation(e.currentTarget)}
      onCanPlay={(e) => emitOrientation(e.currentTarget)}
      onTimeUpdate={onTimeUpdate}
      onPlay={() => {
  setIsPlaying(true);
  setUserPaused(false);
}}
onPause={() => {
  setIsPlaying(false);
}}
//For the replay this onEnded if I dont want replay remove
onEnded={() => {
  const v = videoRef.current;
  if (!v) return;

  v.currentTime = 0;
  v.play().catch(() => {});
}}
      style={{
        width: "100%",
        height: "100%",
        objectFit: "contain",
        display: "block",
        transform: "translateZ(0)",
        backfaceVisibility: "hidden",
        ...style,
      }}
    />

    {/* CONTROLS */}
    <div
      style={{
        position: "absolute",
        bottom: isLandscape ? 0 : 0,
        left: 0,
        width: "100%",
        background: "rgba(0,0,0,0.65)",
        backdropFilter: "blur(6px)",
        opacity: showControls ? 1 : 0,
        transition: "opacity 200ms ease",
        borderBottomLeftRadius: 12,
        borderBottomRightRadius: 12,
        overflow: "hidden",
      }}
      onClick={(e) => e.stopPropagation()} 
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          width: "100%",
          padding: isLandscape ? "7px 14px" : "5px 8px",
        }}
      >
        {/* LEFT — PLAY */}
        <div style={{ display: "flex", alignItems: "center" }}>
    <button
  onPointerDown={(e) => {
    e.stopPropagation();
    togglePlay();
  }}
  style={controlBtn}
>

  {isPlaying ? <PauseIcon /> : <PlayIcon />}

</button>

        </div>

        {/* CENTER — MAIN SCRUBBER */}
<div
  style={{
    flex: "0.9 1 0",      // grow, but don't force max expansion
    display: "flex",
    alignItems: "center",
    padding: "0 8px",   // 👈 smaller side padding
    marginRight: 6,     // 👈 keeps distance from time label
  }}
>
  <input
    type="range"
    min={0}
    max={100}
    value={progress}
    onChange={(e) => handleScrub(Number(e.target.value))}
    className="custom-scrubber"
    style={{
      width: "100%",
      height: 3,
      background: `linear-gradient(
      to right,
      #d8cece 0%,
      #d8cece ${progress}%,
      #555 ${progress}%,
      #555 100%
    )`,
    }}
  />
</div>


        {/* RIGHT — TIME + VOLUME */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            flexShrink: 1,     // ✅ allow shrinking
            minWidth: 0,
          }}
        >
{/* TIME */}
<span
  style={{
    fontSize: 11,
    color: "#bbb",
    whiteSpace: "nowrap",
    marginLeft: -2,
    marginRight: 7,
    fontVariantNumeric: "tabular-nums", // 👈 stable width numbers
  }}
>
  {formatTime(videoRef.current?.currentTime || 0)}/
  {formatTime(videoRef.current?.duration || 0)}
</span>


          {/* SPEAKER */}
          <button
  onClick={(e) => {
    e.stopPropagation();
    setIsMuted(!isMuted);
  }}
  style={controlBtnVolume}
>
            {isMuted ? <SpeakerOffIcon /> : <SpeakerOnIcon />}
          </button>

          {/* VOLUME */}
          <input
            type="range"
            min={0}
            max={1}
            step={0.05}
            value={isMuted ? 0 : volume}
            onChange={(e) => {
              const val = Number(e.target.value);
              setIsMuted(val === 0);
              handleVolume(val);
            }}
            className="volume-slider"
            style={{
              width: isLandscape ? 85 : 65,
              height: 2,
              background: `linear-gradient(
      to right,
      #bcb3b3 0%,
      #d8cece ${(isMuted ? 0 : volume) * 100}%,
      #666 ${(isMuted ? 0 : volume) * 100}%,
      #666 100%
    )`,
            }}
          />
        </div>
      </div>
    </div>
  </div>
);

};

const controlBtn: React.CSSProperties = {
  background: "transparent",
  border: "none",
  color: "#bcb4b4",
  borderRadius: 6,
  cursor: "pointer",
  fontSize: 20,

  display: "flex",
  alignItems: "center",
  justifyContent: "center",

  height: 24,       // 👈 locks height
  lineHeight: 1,    // 👈 prevents font expansion
  padding: 0,       // 👈 removes vertical shift
};

const controlBtnVolume: React.CSSProperties = {
  background: "transparent",
  border: "none",
  color: "#bcb4b4",
  borderRadius: 6,
  cursor: "pointer",
  fontSize: 20,

  display: "flex",
  alignItems: "center",
  justifyContent: "center",

  height: 24,
  lineHeight: 1,
  padding: 0,
};


const GalleryEditPosting = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { postId: postIdFromParams } = useParams<{ postId: string }>();
  const { postId: postIdFromState } = (location.state || {}) as { postId?: string };
  const postId = postIdFromParams || postIdFromState;

  const [isLandscape, setIsLandscape] = React.useState<boolean | null>(null);

  const [isLoading, setIsLoading] = useState(false);
  const { user } = useAuth();
  const { sidebarData }: { sidebarData?: SidebarData } = (location.state || {}) as {
    sidebarData?: SidebarData;
  };

  const [existingMedia, setExistingMedia] = useState<ExtMediaItem[]>([]);
  const [selectedHashtagList, setSelectedHashtagList] = useState<string[]>([]);

  const [successOpen, setSuccessOpen] = useState(false);

  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
const [cancelBusy, setCancelBusy] = useState(false);

  const hasAvatar = Boolean(user?.picture);
  const ROW_TOP_OFFSET = hasAvatar ? 0 : 0;

  // Apply sidebar hashtags only once (if provided)
  useEffect(() => {
  if (!sidebarData?.hashtags) return;

  setSelectedHashtagList(normalizeTagList(sidebarData.hashtags));
}, [sidebarData]);
  

  // Fetch existing media data if postId is provided
  useEffect(() => {
    const fetchMediaData = async () => {
      if (!postId) return;

      try {
        const res = await api.get(`/media/${postId}`);
        const post = res.data?.post ?? res.data ?? {};
        const rawList: any[] = Array.isArray(post.media)
          ? post.media
          : Array.isArray(post.mediaUrls)
          ? post.mediaUrls
          : [];

        const fetched = rawList.map(fromServerMedia) as ExtMediaItem[];
        setExistingMedia(fetched);

      if (Array.isArray(post.hashtags) && post.hashtags.length) {
  setSelectedHashtagList(normalizeTagList(post.hashtags));
}
        
      } catch (err) {
        console.error('Failed to fetch post media:', err);
      }
    };

    fetchMediaData();
  }, [postId]);

  const mediaLabel = (m?: { musicTitle?: string | null; musicArtist?: string | null }) =>
    [m?.musicTitle, m?.musicArtist].filter(Boolean).join(' — ');

  const handleOpenMusic = async (entry: {
    musicId?: string | null;
    musicTitle?: string | null;
    musicArtist?: string | null;
  }) => {
    // 1️⃣ If we already know the track id, go straight there
    if (entry.musicId) {
      navigate(`/musicdetails/${entry.musicId}`);
      return;
    }
  
    // 2️⃣ Fallback: search by title OR artist (not concatenated)
    const q = (entry.musicTitle || entry.musicArtist || '').trim();
    if (!q) {
      console.warn('[GalleryEditPosting.handleOpenMusic] no title/artist', entry);
      return;
    }
  
    try {
      const res = await api.get('/music', { params: { q } });
      const list = Array.isArray(res.data) ? res.data : (res.data?.tracks || []);
      if (!list.length) {
        console.warn('[GalleryEditPosting.handleOpenMusic] no track for', q);
        return;
      }
  
      navigate(`/musicdetails/${list[0]._id}`);
    } catch (e) {
      console.error('[GalleryEditPosting.handleOpenMusic] lookup failed:', e);
    }
  };

  const handleSuccessClose = () => {
  setSuccessOpen(false);
  navigate('/my-posts', { state: { reset: true } });
};
  
  const handleDelete = (id: string) => {
    setExistingMedia((prev) => prev.filter((item) => item.id !== id));
  };

  // Posting to Gallery (save updated media list)
  const postingRef = React.useRef(false);

  const handleSaveEdit = async () => {
    if (!postId) {
      alert(t('gallery.edit.missingPostId'));
      return;
    }
    if (postingRef.current) return;

    postingRef.current = true;
    setIsLoading(true);
    try {
      await proceedWithEdit();
    } catch (err) {
      console.error('❌ Error in handleSaveEdit:', err);
    } finally {
      postingRef.current = false;
      setIsLoading(false);
    }
  };

  const proceedWithEdit = async () => {
    if (!postId) return;
    try {
      const mediaEntries = existingMedia.map((m) =>
  m.kind === 'video'
    ? {
        isHls: true,
        videoId: m.videoId ?? null,
        hlsUrl: m.hlsUrl ?? null,
        dashUrl: m.dashUrl ?? null,
        posterUrl: m.posterUrl ?? null,
        musicTitle: m.musicTitle ?? null,
        musicArtist: m.musicArtist ?? null,
        musicId: m.musicId ?? null,
      }
    : {
        url: m.url,
      }
);


     const payload = {
  media: mediaEntries,
  hashtags: selectedHashtagList,
};

     await api.put(`/media/${postId}`, payload);
     setSuccessOpen(true);
     setTimeout(() => {
  navigate('/my-posts', { state: { reset: true } });
}, 5000);
    } catch (err) {
      console.error('❌ Failed to update post:', err);
      alert(t('gallery.edit.saveFailed'));
    }
  };
const handleCancel = () => {
  setCancelConfirmOpen(true);
};

const confirmCancel = () => {
  setCancelBusy(true);

  setSelectedHashtagList([]);

  navigate('/my-posts', { replace: true });
};

useEffect(() => {
  const style = document.createElement('style');
  style.setAttribute('data-hide-scrollbars', 'true');
  style.innerHTML = `
    .hScroll {
      scrollbar-width: none;
      -ms-overflow-style: none;
    }
    .hScroll::-webkit-scrollbar {
      display: none;
      width: 0;
      height: 0;
    }
  `;
  document.head.appendChild(style);
  return () => {
    document.head.removeChild(style);
  };
}, []);

  const LoadingOverlay = () => (
    <div style={loadingOverlayStyle}>
      <div style={loadingContentStyle}>
        <span>{t('gallery.edit.saving')}</span>
        <div style={loadingDotsContainer}>
          <span style={loadingDot}>.</span>
          <span style={loadingDot}>.</span>
          <span style={loadingDot}>.</span>
        </div>
      </div>
    </div>
  );

return (
  <>
    {isLoading && <LoadingOverlay />}

    <div style={pageShellStyle}>
      {/* HEADER – same as GalleryPosting */}
     <header style={pageHeaderStyle}>
  <div style={pageHeaderLeft}>
    <HighLogo
      src={logo}
      style={{ width: '12.2%', display: 'block' }}
      padding={0}
      onClick={() => navigate('/gallery')}
    />
  </div>
</header>

      {/* MAIN CONTENT */}
      <main style={pageMainStyle}>
        <div style={cardGridStyle}>
          {/* Sidebar */}
          <aside style={sidebarContainerStyle}>
            <div style={{ top: 76}} >
            <SidebarGalleryEditPosting
              selectedHashtags={selectedHashtagList}
              onHashtagChange={(tags: string[]) => {
  setSelectedHashtagList(normalizeTagList(tags));
}}
              onCancel={handleCancel}
              onPost={handleSaveEdit}
              isPostDisabled={existingMedia.length === 0}
            />
            </div>
          </aside>

          {/* Left bottom terms privacy help buttons */}
<div
  style={{
    position: "fixed",
    left: 18,
    bottom: 8,
    width: 230,
    zIndex: 50,
    fontSize: 10,
    color: "#555",
    lineHeight: 1.6,
    textAlign: "left",
    pointerEvents: "auto",
  }}
>
  <span
    style={{ cursor: "pointer" }}
    onClick={() => navigate("/helpsupportstart")}
  >
    {t("mainSidebar.footer.helpSupport")}
  </span>

  {" · "}

  <span
    style={{ cursor: "pointer" }}
    onClick={() => navigate("/legal/privacypolicy")}
  >
    {t("mainSidebar.footer.privacyPolicy")}
  </span>

  {" · "}

  <span
    style={{ cursor: "pointer" }}
    onClick={() => navigate("/legal/termsofuse")}
  >
    {t("mainSidebar.footer.termsOfUse")}
  </span>
</div>

          {/* Right-side content */}
          <section style={mediaOuterStyle}>
            <div style={postCardStyle}>
              {/* Header / avatar row */}
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  marginBottom: 0,
                  marginLeft: "15px",
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <div
                    style={{
                      width: 35,
                      height: 35,
                      borderRadius: '5%',
                      marginRight: 10,
                      backgroundColor: user?.picture ? 'transparent' : 'rgb(38, 29, 162)',
                      backgroundImage: user?.picture ? `url(${user.picture})` : 'none',
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: user?.picture ? 0 : '20px',
                    }}
                  >
                    {!user?.picture && '👻'}
                  </div>
                  <strong>{user?.profilename || t('gallery.edit.anonymous')}</strong>
                </div>
              </div>

              {/* Scrollable media row */}
              <div style={{ ...mediaScrollerStyle, marginTop: ROW_TOP_OFFSET, }}
                className="hScroll"
               >
                
                {existingMedia.map((item, index) => (
                  <div
                    key={item.id}
                    style={{
                      flex: '0 0 auto',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'flex-start',
                      background: 'transparent',
                      position: 'relative',
                      boxSizing: 'border-box',
                      margin: 0,
                      padding: 0,
                    }}
                  >
                    {/* VIDEO */}
                    {item.kind === 'video' ? (
                      <div
                        style={{
                          position: 'relative',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                        }}
                      >
  <HLSPlayer
  src={resolveVideoSrc(item)}
  muted
  active
  onOrientation={(land) => setIsLandscape(land)}
style={{
  width: 'auto',
  height: 'auto',
  maxHeight: '81vh',

  maxWidth: isLandscape
    ? '88vh'
    : 'min(540px, 44vw)',

  borderRadius: 12,
}}
/>


                        {/* 🎵 MUSIC LINK */}
                        {(item.musicTitle || item.musicArtist) && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenMusic({
                                musicId: item.musicId,
                                musicTitle: item.musicTitle,
                                musicArtist: item.musicArtist,
                              });
                            }}
                            style={{
                              position: 'absolute',
                              left: 0,
                              right: 0,
                              bottom: 0,
                              transform: 'translateY(100%)',
                              padding: '6px 10px 10px',
                              fontSize: 12,
                              color: '#666',
                              background:
                                'linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.35) 40%, rgba(0,0,0,0.6) 100%)',
                              border: 'none',
                              cursor: 'pointer',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            🎵 {[item.musicTitle, item.musicArtist].filter(Boolean).join(' — ')}
                          </button>
                        )}
                      </div>
                    ) : item.url ? (
<img
  src={item.url}
  alt={`media-${index}`}
  loading="lazy"
  style={{
  width: 'auto',
  height: 'auto',
  maxHeight: '81vh',

  maxWidth: isLandscape
    ? '88vh'
    : 'min(540px, 44vw)',

  objectFit: 'contain',
  borderRadius: 12,
}}
/>
                    ) : (
                      <div style={{ color: '#bbb' }}>
                        {t('gallery.edit.noPreview')}
                      </div>
                    )}

                    {/* Actions */}
                    <div
                      style={{
                        width: '50%',
                        display: 'flex',
                        gap: 10,
                        justifyContent: 'center',
                        flexWrap: 'nowrap',
                        marginTop: 10,
                      }}
                    >
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(item.id);
                        }}
                        style={smallActionButton}
                      >
                        {t('gallery.edit.delete')}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </div>
      </main>

      {cancelConfirmOpen && (
        <div style={confirmOverlay}>
          <div style={confirmCard} onClick={(e) => e.stopPropagation()}>
            <div style={{ marginBottom: 14, fontSize: 16, fontWeight: 600 }}>
              {t('gallery.edit.discardConfirm')}
            </div>

            <button
              onClick={confirmCancel}
              disabled={cancelBusy}
              style={primaryBtn}
            >
              {t('common.confirm')}
            </button>

            <button
              onClick={() => setCancelConfirmOpen(false)}
              disabled={cancelBusy}
              style={cancelBtn}
            >
              {t('common.cancel')}
            </button>
          </div>
          
        </div>
        
      )}

            {successOpen && (
        <Modal
          open={successOpen}
          onClose={handleSuccessClose}
          title={t('gallery.edit.updatedTitle', 'Post updated')}
          width={380}
        >
          <div style={{ textAlign: 'center', fontSize: 14, color: '#ddd' }}>
            ✅ {t('gallery.edit.updatedMessage', 'Your post was updated successfully.')}
          </div>
      
          <button
            onClick={handleSuccessClose}
            style={{
              width: '100%',
              marginTop: 14,
              padding: '10px',
              background: '#22c55e',
              border: 'none',
              borderRadius: 8,
              color: '#fff',
              cursor: 'pointer',
              fontWeight: 600,
            }}
          >
            {t('common.ok', 'OK')}
          </button>
        </Modal>
      )}
      
    </div>
  </>
  
);
};

/* --- constants & styles copied / adapted from GalleryPosting --- */


/* 🌌 Page shell */
const pageShellStyle: React.CSSProperties = {
  paddingTop: 25,  
  background:
    'radial-gradient(circle at top, #020617 0, #000 40%, #020617 100%)',
  color: '#f9fafb',
  fontFamily:
    'system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif',
};

/* 🧭 Header */
const pageHeaderStyle: React.CSSProperties = {
  position: 'absolute',
  top: '21px',
  left: '-8px',
  right: 0,
  zIndex: 30,
  display: 'flex',
  alignItems: 'center',
  background: 'transparent',
};

const pageHeaderLeft: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  cursor: 'pointer',
};

/* 🧱 Main */
const pageMainStyle: React.CSSProperties = {
  flex: 1,
  padding: '0px 10px 0 24px',   // ⬅ remove vertical padding
};

/* Grid: sidebar + composer */
const cardGridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '300px minmax(0, 1fr)',
  gap: 0,
  alignItems: 'start',
  marginLeft: '-18px', 
};

/* Sidebar container */
const sidebarContainerStyle: React.CSSProperties = {
  position: 'sticky',
  overflow: 'visible', 
  marginTop: "50px",
};

/* Composer area outer */
const mediaOuterStyle: React.CSSProperties = {
  width: '100%',
  alignSelf: 'start',
};

/* Composer card */
const postCardStyle: React.CSSProperties = {
  width: '100%',
  height: '100%',
  background: 'transparent',
  padding: 0,
  borderRadius: 0,
  border: 'none',
  boxShadow: 'none',
  color: '#e5e7eb',
  display: 'flex',
  flexDirection: 'column',
  marginLeft: '-15px',  // Move the entire card to the left by 10px
};

/* Horizontal media scroller */
const mediaScrollerStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'row',
  overflowX: 'auto',
  overflowY: 'hidden',
  gap: 40,
  width: '100%',
  alignItems: 'flex-start',
  justifyContent: 'flex-start',
  paddingTop: 15,
  paddingLeft: '15px',  // Shift content 10px to the right (create buffer zone)
};

/* Small action buttons under each media */
const smallActionButton: React.CSSProperties = {
  padding: '4px 8px',
  backgroundColor: '#020617',
  border: '1px solid #cbd5f5',
  borderRadius: 999,
  color: '#cbd5f5',
  cursor: 'pointer',
  fontSize: 12,
  fontWeight: 500,
  marginTop: 32,
};

/* Loading overlay */
const loadingOverlayStyle: React.CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  backgroundColor: 'rgba(255, 255, 255, 0.9)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  zIndex: 1000,
};

const loadingContentStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  fontSize: '24px',
  color: '#380675',
};

const loadingDotsContainer: React.CSSProperties = {
  display: 'flex',
  gap: '5px',
};

const loadingDot: React.CSSProperties = {
  animation: 'loadingDots 1s infinite',
  fontSize: '36px',
};

const confirmOverlay: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.65)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 99999999,
};

const confirmCard: React.CSSProperties = {
  width: "92%",
  maxWidth: 420,
  background: "#020617",
  borderRadius: 18,
  padding: 22,
  textAlign: "center",
  border: "1px solid rgba(148,163,184,0.35)",
  color: "#e5e7eb",
  boxShadow: "0 25px 80px rgba(0,0,0,0.9)",
};

const primaryBtn: React.CSSProperties = {
  width: "100%",
  padding: 14,
  borderRadius: 12,
  border: "none",
  background: "#dc2626",
  color: "#fff",
  fontWeight: 700,
  marginBottom: 10,
  cursor: "pointer",
};

const cancelBtn: React.CSSProperties = {
  width: "100%",
  padding: 12,
  borderRadius: 12,
  border: "1px solid rgba(148,163,184,0.35)",
  background: "transparent",
  color: "#cbd5f5",
  cursor: "pointer",
};


export default GalleryEditPosting;
